// FILE: ProviderModelPicker.logic.ts
// Purpose: Pure helpers behind the provider/model picker — building the flat
//          agent section, resolving provider availability and the trigger's
//          label — kept apart from the component so they can be unit tested
//          without mounting React.
// Exports: `filterAgentsByProvider`, `findSelectedAgent`, `resolvePickerTriggerLabel`,
//          `resolveLiveProviderAvailability`, `resolvePickerAgentEntries`,
//          `resolvePickerModelProviders`.

import type {
  ManagedAgent,
  ManagedAgentId,
  ProviderKind,
  ServerProviderStatus,
} from "@taskin-code/contracts";
import { canRunAsPrimary } from "@taskin-code/shared/managedAgentMode";

/**
 * Agents belonging to one provider.
 *
 * Only used for the locked-provider case now that agents are listed flat: a
 * thread that already started is pinned to its session's provider, so offering
 * an agent from another one would promise something that cannot be honoured.
 */
export function filterAgentsByProvider(
  agents: ReadonlyArray<ManagedAgent>,
  provider: ProviderKind,
): ReadonlyArray<ManagedAgent> {
  return agents.filter((agent) => agent.provider === provider);
}

/**
 * The agent the composer currently has selected, if any and if it is still
 * offered. A `selectedAgentId` pointing at an agent that was deleted or fell
 * out of visibility resolves to `null` rather than throwing — the trigger
 * then falls back to the plain model label, same as if no agent had ever
 * been picked.
 */
export function findSelectedAgent(
  agents: ReadonlyArray<ManagedAgent>,
  selectedAgentId: ManagedAgentId | null | undefined,
): ManagedAgent | null {
  if (!selectedAgentId) return null;
  return agents.find((agent) => agent.id === selectedAgentId) ?? null;
}

/**
 * The trigger shows the agent's own name instead of its model slug when one
 * is selected — an agent is the thing the user picked, the model is just an
 * implementation detail of it.
 */
export function resolvePickerTriggerLabel(
  selectedAgent: ManagedAgent | null,
  modelLabel: string,
): string {
  return selectedAgent ? selectedAgent.name : modelLabel;
}

/**
 * Whether a provider can be picked right now, and what to say when it cannot.
 *
 * Lives here rather than in the component because the flat agent list needs the
 * same answer per agent: an agent is only as usable as the provider underneath
 * it, and that verdict now has to be reached in two places instead of one.
 */
export function resolveLiveProviderAvailability(provider: ServerProviderStatus | undefined): {
  disabled: boolean;
  label: string | null;
} {
  if (!provider) {
    return { disabled: false, label: null };
  }

  if (!provider.available) {
    return {
      disabled: true,
      // "Sign in" reads as an action, so only use it when one is actually
      // offered — an uninstalled CLI cannot be fixed by logging in.
      label: provider.authStatus === "unauthenticated" ? "Sign in" : "Unavailable",
    };
  }

  if (provider.authStatus === "unauthenticated") {
    return { disabled: true, label: "Sign in" };
  }

  return { disabled: false, label: null };
}

export interface PickerAgentEntry {
  readonly agent: ManagedAgent;
  /** True when the agent's provider cannot serve a session right now. */
  readonly disabled: boolean;
  /** "Sign in" / "Unavailable", or `null` when the agent is usable. */
  readonly availabilityLabel: string | null;
  /** Whether the disabled reason is a missing login, which Settings can fix. */
  readonly needsSignIn: boolean;
}

/**
 * The agent section of the menu: one flat list, not grouped by provider.
 *
 * Agents carry their own provider and model, so filing them under a provider
 * submenu exposed an implementation detail as if it were the way to navigate —
 * the trigger already shows the agent's name for exactly that reason. Flattening
 * them makes "pick an agent" a first-class choice and "pick a raw model" the
 * secondary one.
 *
 * `lockedProvider` is the one case where the provider still filters: a thread
 * that has already started is pinned to its session's provider, and switching
 * would need the session restarted, so agents from other providers are not
 * offered at all rather than offered and silently ignored.
 *
 * Availability is resolved per agent because a flat list mixes providers: with
 * Codex signed out, its agents have to say so on their own row now that there is
 * no Codex submenu to carry that state for them.
 *
 * Mode filters the list too, and it is filtered *here* rather than in the hook
 * that fetches the agents: that list is also what labels delegation rows in the
 * transcript, so it deliberately still carries subagent-only agents. The one
 * exception is `selectedAgentId` — a delegated thread runs on exactly such an
 * agent, and dropping it would make its own picker show a bare model slug and
 * offer no way back to the selection it already has.
 */
export function resolvePickerAgentEntries(input: {
  readonly agents: ReadonlyArray<ManagedAgent>;
  readonly providers: ReadonlyArray<ServerProviderStatus> | undefined;
  readonly lockedProvider: ProviderKind | null;
  readonly selectedAgentId?: ManagedAgentId | null | undefined;
}): ReadonlyArray<PickerAgentEntry> {
  const pickable = input.agents.filter(
    (agent) => canRunAsPrimary(agent.mode) || agent.id === input.selectedAgentId,
  );
  const scoped =
    input.lockedProvider === null
      ? pickable
      : filterAgentsByProvider(pickable, input.lockedProvider);

  return [...scoped]
    .sort((left, right) => left.name.localeCompare(right.name))
    .map((agent) => {
      const liveProvider = input.providers?.find((entry) => entry.provider === agent.provider);
      const availability = resolveLiveProviderAvailability(liveProvider);
      return {
        agent,
        disabled: availability.disabled,
        availabilityLabel: availability.label,
        needsSignIn: liveProvider?.authStatus === "unauthenticated",
      };
    });
}

/**
 * The providers worth listing under "Models".
 *
 * A provider whose model list came back empty after the per-user allowlist
 * filter is a dead end: the submenu would open onto "No models available", and
 * for the non-technical profile — an agent granted, no models at all — that was
 * every provider in the menu. Dropping them keeps the menu to what the account
 * can actually act on.
 *
 * Emptiness is judged after the allowlist rather than against the provider's
 * catalogue, so a provider the user is simply not signed into still shows up as
 * long as they have models granted there: hiding it would remove the only way
 * they have to reach the "Sign in" row.
 */
export function resolvePickerModelProviders<T extends { readonly value: ProviderKind }>(input: {
  readonly providerOptions: ReadonlyArray<T>;
  readonly modelOptionsByProvider: Record<ProviderKind, ReadonlyArray<{ readonly slug: string }>>;
}): ReadonlyArray<T> {
  return input.providerOptions.filter(
    (option) => (input.modelOptionsByProvider[option.value]?.length ?? 0) > 0,
  );
}
